
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Contact() {
  useEffect(() => {
    // Add SEO meta tags dynamically
    document.title = 'Contact Us - Free Consultation | Central Demolition & Services';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contact Central Demolition & Services for your free consultation. Call 781-408-9479 or email contact@central-demolition.com. Serving all of New England.');
    }

    // Add Schema.org JSON-LD for Contact page
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Central Demolition & Services",
      "description": "Get in touch for your free demolition consultation",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/contact`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Central Demolition & Services",
        "telephone": "857-777-4300",
        "email": "contact@central-demolition.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "PO Box - 321",
          "addressLocality": "Stoughton",
          "addressRegion": "MA",
          "postalCode": "02072",
          "addressCountry": "US"
        },
        "openingHours": "Mo-Sa 07:00-18:00",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "857-777-4300",
          "contactType": "customer service",
          "email": "contact@central-demolition.com",
          "availableLanguage": "English"
        }
      }
    });
    document.head.appendChild(schemaScript);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Let's Start Something <span className="text-blue-600">Strong</span> Together.
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Ready to discuss your demolition project? Contact us today for a free consultation 
              and discover how our experienced team can help you achieve your goals safely and efficiently.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            
            {/* Contact Details */}
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-8">Get In Touch</h2>
              <p className="text-lg text-gray-600 mb-12">
                We're here to help with your demolition and general labor needs. 
                Choose the most convenient way to reach out to us:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-blue-50 p-8 rounded-lg border-l-4 border-blue-600 text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-mail-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Email Us</h3>
                  <a href="mailto:contact@central-demolition.com" className="text-blue-600 hover:text-blue-700 text-xl font-semibold cursor-pointer block mb-2">
                    contact@central-demolition.com
                  </a>
                  <p className="text-gray-600 mb-4">We typically respond within 2-4 hours</p>
                  <a href="mailto:contact@central-demolition.com" className="inline-block">
                    <Button size="lg">
                      <i className="ri-mail-send-fill mr-2"></i>
                      Send Email
                    </Button>
                  </a>
                </div>

                <div className="bg-green-50 p-8 rounded-lg border-l-4 border-green-600 text-center">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-phone-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Call Us</h3>
                  <a href="tel:857-777-4300" className="text-green-600 hover:text-green-700 text-3xl font-bold cursor-pointer block mb-2">
                    857-777-4300
                  </a>
                  <p className="text-gray-600 mb-4">Mon–Fri: 7:00 AM – 6:00 PM</p>
                  <a href="tel:857-777-4300" className="inline-block">
                    <Button size="lg" className="bg-green-600 hover:bg-green-700">
                      <i className="ri-phone-fill mr-2"></i>
                      Call Now
                    </Button>
                  </a>
                </div>
              </div>

              {/* Additional Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-map-pin-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Service Area</h3>
                  <p className="text-gray-700 text-lg">All of New England</p>
                  <p className="text-gray-600">Massachusetts • Rhode Island • Connecticut<br />New Hampshire • Vermont • Maine</p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-time-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Business Hours</h3>
                  <p className="text-gray-700 text-lg">Monday – Saturday</p>
                  <p className="text-gray-600">7:00 AM – 6:00 PM</p>
                  <p className="text-gray-500 text-sm mt-1">Emergency services available</p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-home-2-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Mailing Address</h3>
                  <p className="text-gray-700 text-lg">PO Box - 321 Stoughton, MA 02072</p>
                  <p className="text-gray-600">United States</p>
                </div>
              </div>
            </div>

            {/* Quick Information */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">When You Contact Us</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-question-answer-line text-blue-600"></i>
                  </div>
                  <h4 className="font-bold mb-2 text-white">Free Consultation</h4>
                  <p className="text-white text-sm">Detailed discussion about your project needs and requirements</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-calculator-line text-blue-600"></i>
                  </div>
                  <h4 className="font-bold mb-2 text-white">Accurate Quote</h4>
                  <p className="text-white text-sm">Transparent pricing with no hidden fees or surprises</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-calendar-check-line text-blue-600"></i>
                  </div>
                  <h4 className="font-bold mb-2 text-white">Project Planning</h4>
                  <p className="text-white text-sm">Timeline coordination and permit assistance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Quick answers to common questions about our services and process.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">How quickly can you start a project?</h3>
              <p className="text-gray-600">
                Most projects can begin within 1-2 weeks after consultation, depending on permits 
                and project complexity. Emergency services are available for urgent needs.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Do you handle permits and inspections?</h3>
              <p className="text-gray-600">
                Yes, we assist with all necessary permits and coordinate with local authorities 
                for inspections as part of our comprehensive service.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Are you fully insured?</h3>
              <p className="text-gray-600">
                Absolutely. We carry comprehensive liability insurance and workers' compensation 
                coverage for all our operations across New England.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">What makes your lab services special?</h3>
              <p className="text-gray-600">
                Our team has specialized training in handling sensitive laboratory equipment 
                and hazardous materials with proper containment and disposal protocols.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
  
  {/* Removido o script Neurolead duplicado - já está no index.html */}
</div>
  );
}
